import React, { useState, useRef, useEffect, useCallback } from "react";
import config from "../../config";
import { features } from "../../config/features";

const TOKEN_PARAM = "token";

const STATUS = {
  INIT: "init",
  CAMERA: "camera",
  PREVIEW: "preview",
  UPLOADING: "uploading",
  SUCCESS: "success",
  ERROR: "error",
};

export const MobileCapturePage = () => {
  const token = new URLSearchParams(window.location.search).get(TOKEN_PARAM);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const [status, setStatus] = useState(STATUS.INIT);
  const [captured, setCaptured] = useState(null); // data-URL
  const [errorMsg, setErrorMsg] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  // Stop camera tracks when no longer needed
  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  // Start camera
  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setStatus(STATUS.CAMERA);
    } catch (err) {
      setCameraError(
        err.name === "NotAllowedError"
          ? "Camera access was denied. Please allow camera access in your browser settings and try again."
          : "Could not access the camera. Make sure it is not in use by another app."
      );
    }
  }, []);

  // Attach stream to video element once CAMERA status renders the <video>
  useEffect(() => {
    if (status === STATUS.CAMERA && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [status]);

  useEffect(() => {
    if (!features.identityVerification) {
      window.location.replace("/");
      return;
    }
    if (!token) {
      setErrorMsg("No session token found. Please scan a valid QR code.");
      setStatus(STATUS.ERROR);
    }
  }, [token]);

  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
    setCaptured(dataUrl);
    stopCamera();
    setStatus(STATUS.PREVIEW);
  }, [stopCamera]);

  const handleRetake = useCallback(() => {
    setCaptured(null);
    setStatus(STATUS.INIT);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!captured || !token) return;
    setStatus(STATUS.UPLOADING);
    try {
      const byteString = atob(captured.split(",")[1]);
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
      const blob = new Blob([ab], { type: "image/jpeg" });
      const file = new File([blob], `mobile-selfie-${Date.now()}.jpg`, { type: "image/jpeg" });

      const formData = new FormData();
      formData.append("image", file);

      const url = `${config.API_ROOT_URL}/face-verification/session/${token}`;
      const res = await fetch(url, { method: "PUT", body: formData });

      if (res.ok || res.status === 200) {
        setStatus(STATUS.SUCCESS);
      } else {
        const body = await res.json().catch(() => null);
        const msg = body?.message ?? body?.Message ?? `Upload failed (${res.status}).`;
        if (res.status === 404 || res.status === 410) {
          setErrorMsg("This QR session has expired or was already used. Please go back to the desktop and generate a new code.");
        } else {
          setErrorMsg(msg);
        }
        setStatus(STATUS.ERROR);
      }
    } catch {
      setErrorMsg("A network error occurred. Please check your connection and try again.");
      setStatus(STATUS.ERROR);
    }
  }, [captured, token]);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-start p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden mt-4">
        {/* Header */}
        <div className="bg-[#9aa82d] px-6 py-5 flex items-center gap-3">
          <i className="fal fa-id-badge text-white text-2xl"></i>
          <h1 className="text-lg font-semibold text-white">Identity Verification</h1>
        </div>

        <div className="p-6 flex flex-col items-center gap-6">

          {/* ── INIT: prompt to open camera ── */}
          {status === STATUS.INIT && (
            <div className="flex flex-col items-center gap-6 py-6 text-center w-full">
              <div className="w-20 h-20 rounded-full bg-[#f5f7eb] flex items-center justify-center">
                <i className="fal fa-camera text-[#9aa82d] text-4xl"></i>
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-800 mb-2">Take a Selfie</p>
                <p className="text-base text-gray-500">
                  We need a clear photo of your face to verify your identity. Make sure you are in a well-lit area.
                </p>
              </div>
              {cameraError && (
                <div className="w-full bg-red-50 border border-red-200 rounded-lg p-4 text-left">
                  <p className="text-sm text-red-600">{cameraError}</p>
                </div>
              )}
              <button
                type="button"
                onClick={startCamera}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold text-white bg-[#9aa82d] hover:bg-[#7d8a1f] active:bg-[#6b7519] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
              >
                <i className="fal fa-camera text-lg"></i>
                Open Camera
              </button>
            </div>
          )}

          {/* ── CAMERA: live viewfinder ── */}
          {status === STATUS.CAMERA && (
            <div className="flex flex-col items-center gap-5 w-full">
              <p className="text-base text-gray-500 text-center">
                Centre your face in the oval and tap <strong>Capture</strong>.
              </p>

              <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ aspectRatio: "3/4" }}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                  style={{ transform: "scaleX(-1)" }}
                />
                {/* Face guide */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div
                    className="rounded-full border-2 border-white border-opacity-70"
                    style={{ width: "60%", height: "55%", boxShadow: "0 0 0 9999px rgba(0,0,0,0.30)" }}
                  />
                </div>
              </div>

              <canvas ref={canvasRef} className="hidden" />

              <button
                type="button"
                onClick={handleCapture}
                className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-base font-semibold text-white bg-[#9aa82d] hover:bg-[#7d8a1f] active:bg-[#6b7519] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
              >
                <i className="fas fa-camera text-lg"></i>
                Capture
              </button>
            </div>
          )}

          {/* ── PREVIEW: review before submit ── */}
          {status === STATUS.PREVIEW && captured && (
            <div className="flex flex-col items-center gap-5 w-full">
              <p className="text-base text-gray-500 text-center">
                Does this look good? Tap <strong>Verify and Send</strong> to send, or <strong>Retake</strong> to try again.
              </p>

              <div className="w-full rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ aspectRatio: "3/4" }}>
                <img
                  src={captured}
                  alt="selfie preview"
                  className="w-full h-full object-cover"
                  style={{ transform: "scaleX(-1)" }}
                />
              </div>

              <div className="flex gap-3 w-full">
                <button
                  type="button"
                  onClick={handleRetake}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl text-base font-semibold border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <i className="fal fa-redo text-lg"></i>
                  Retake
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl text-base font-semibold text-white bg-[#9aa82d] hover:bg-[#7d8a1f] active:bg-[#6b7519] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d]"
                >
                  <i className="fas fa-paper-plane text-lg"></i>
                  Verify and Send
                </button>
              </div>
            </div>
          )}

          {/* ── UPLOADING ── */}
          {status === STATUS.UPLOADING && (
            <div className="flex flex-col items-center gap-5 py-14 text-center">
              <i className="fas fa-circle-notch fa-spin text-5xl text-[#9aa82d]"></i>
              <p className="text-base text-gray-600">Sending photo…</p>
            </div>
          )}

          {/* ── SUCCESS ── */}
          {status === STATUS.SUCCESS && (
            <div className="flex flex-col items-center gap-6 py-12 text-center">
              <div className="w-24 h-24 rounded-full bg-green-100 flex items-center justify-center">
                <i className="fas fa-check text-green-600 text-5xl"></i>
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900 mb-2">Photo sent!</p>
                <p className="text-base text-gray-500">
                  Your selfie has been received. You can close this tab and return to your desktop.
                </p>
              </div>
            </div>
          )}

          {/* ── ERROR ── */}
          {status === STATUS.ERROR && (
            <div className="flex flex-col items-center gap-6 py-12 text-center">
              <div className="w-24 h-24 rounded-full bg-red-100 flex items-center justify-center">
                <i className="fas fa-times text-red-500 text-5xl"></i>
              </div>
              <div>
                <p className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</p>
                <p className="text-base text-gray-500">{errorMsg ?? "An unexpected error occurred."}</p>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default MobileCapturePage;
